<template>
  <div v-if="loading" class="flex justify-center items-center h-screen">
    <p class="text-xl font-semibold">Loading patient data...</p>
  </div>
  <div v-else class="bg-white p-8 rounded-lg shadow-md max-w-7xl mx-auto">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-800">Patient Information</h1>
      <button @click="toggleEditMode" class="text-custom-yellow">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </button>
    </div>

    <div class="grid grid-cols-3 gap-x-8 gap-y-4 mb-8">
      <div>
        <p class="text-sm text-gray-600">Title</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.title || "-" }}</p>
        <select
          v-if="isEditing"
          v-model="patientData.title"
          @input="updateForm"
          id="patient-title"
          class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option disabled value="">Please select a title</option>
          <option v-for="title in titles" :key="title" :value="title">{{ title }}</option>
        </select>
      </div>
      <div>
        <p class="text-sm text-gray-600">ID/Passport Number</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.idNumber || "-" }}</p>
        <input v-else v-model="patientData.idNumber" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Patient Unique Code</p>
        <p class="font-medium">{{ patientData.unique_code || "-" }}</p>
      </div>
      <div>
        <p class="text-sm text-gray-600">Phone Number</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.phoneNumber || "-" }}</p>
        <input type="text" v-else v-model="patientData.phoneNumber" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Name</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.fullName || "-" }}</p>
        <input v-else v-model="patientData.fullName" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Email</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.emailAddress || "-" }}</p>
        <input v-else v-model="patientData.emailAddress" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Gender</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.gender || "-" }}</p>
        <select
          v-else
          v-model="patientData.gender"
          @input="updateForm"
          id="patient-gender"
          class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option disabled value="">Please select gender</option>
          <option v-for="gender in genders" :key="gender" :value="gender">{{ gender }}</option>
        </select>
      </div>
      <div>
        <p class="text-sm text-gray-600">D.O.B</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.dob || "-" }}</p>
        <input type="date" v-else v-model="patientData.dob" @change="calculateAgeFromDob" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Age</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.age || "-" }}</p>
        <input type="text" v-else v-model="patientData.age" @change="calculateDobFromAge" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Residence</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.residence || "-" }}</p>
        <input v-else v-model="patientData.residence" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Nationality</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.nationality || "-" }}</p>
        <input type="text" v-else v-model="patientData.nationality" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">County</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.county || "-" }}</p>
        <input v-else v-model="patientData.county" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Occupation</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.occupation || "-" }}</p>
        <input type="text" v-else v-model="patientData.occupation" class="w-full border rounded px-2 py-1 mt-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Marital Status</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.maritalStatus || "-" }}</p>
        <select
          v-else
          v-model="patientData.maritalStatus"
          @input="updateForm"
          id="patient-marital-status"
          class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-sm focus:ring-blue-500 focus:border-blue-500"
        >
          <option disabled value="">Please select marital status</option>
          <option v-for="status in maritalStatuses" :key="status" :value="status">{{ status }}</option>
        </select>
      </div>
    </div>

    <!-- Next of Kin -->
    <h3 class="text-xl font-semibold text-gray-800 mb-4">Next of Kin</h3>
    <div v-if="patientData.emergency_contact" class="grid grid-cols-3 gap-x-8 gap-y-4 mb-8">
      <div>
        <p class="text-sm text-gray-600">Full Name:</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.emergency_contact.Name || "-" }}</p>
        <input v-else v-model="patientData.emergency_contact.Name" class="w-full border rounded px-2 py-1" />
      </div>
      <div>
        <p class="text-sm text-gray-600">Relationship To Patient:</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.emergency_contact.RelationshipToPatient || "-" }}</p>
        <select
          v-else
          v-model="patientData.emergency_contact.RelationshipToPatient"
          id="emergency-relationship"
          class="w-full px-3 py-2 mt-1 text-sm bg-gray-100 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        >
          <option disabled value="">Select relationship</option>
          <option v-for="relationship in relationships" :key="relationship" :value="relationship">{{ relationship }}</option>
        </select>
      </div>
      <div>
        <p class="text-sm text-gray-600">Phone Number:</p>
        <p v-if="!isEditing" class="font-medium">{{ patientData.emergency_contact.PhoneNumber || "-" }}</p>
        <input v-else v-model="patientData.emergency_contact.PhoneNumber" class="w-full border rounded px-2 py-1" />
      </div>
    </div>
    <div v-else class="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <p class="text-gray-500 italic text-center">No emergency contact information provided</p>
    </div>

    <!-- Drug Allergies -->
    <h3 class="text-xl font-semibold text-gray-800 mb-4">Drug Allergies</h3>
    <div v-if="drug_allergies && drug_allergies.length > 0" class="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
      <div class="flex flex-wrap gap-2">
        <span
          v-for="allergy in drug_allergies"
          :key="allergy.id"
          class="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-300"
        >
          {{ allergy.inventory_item?.trade_name || allergy.name }}
          <span v-if="allergy.drug_category" class="ml-1 text-xs text-red-600">({{ allergy.drug_category.name }})</span>
        </span>
      </div>
    </div>
    <div v-else class="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
      <p class="text-gray-500 italic text-center">No known drug allergies recorded</p>
    </div>

    <!-- Payment Information -->
    <h3 class="text-xl font-semibold text-gray-800 mb-4">Payment Information</h3>
    <div v-if="pre_payment_methods && pre_payment_methods.length > 0" class="mb-6">
      <h4 class="text-lg font-semibold text-gray-800 mb-2">Pre-Payment Methods</h4>
      <table class="table-auto w-full border border-gray-300">
        <thead>
          <tr class="bg-gray-100">
            <th class="border px-4 py-2">No</th>
            <th class="border px-4 py-2">Payment Method</th>
            <th class="border px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(method, index) in pre_payment_methods" :key="index">
            <td class="border text-center px-4 py-2">{{ index + 1 }}</td>
            <td class="border text-center px-4 py-2">{{ formatPaymentMethod(method.payment_method) }}</td>
            <td class="border text-center px-4 py-2">
              <span :class="method.is_active ? 'text-green-600' : 'text-red-600'">
                {{ method.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="post_payment_methods && post_payment_methods.length > 0" class="mb-6">
      <h4 class="text-lg font-semibold text-gray-800 mb-2">Post-Payment Methods (FFS)</h4>
      <table class="table-auto w-full border border-gray-300">
        <thead>
          <tr class="bg-gray-100">
            <th class="border px-4 py-2">No</th>
            <th class="border px-4 py-2">Company</th>
            <th class="border px-4 py-2">Beneficiary</th>
            <th class="border px-4 py-2">Member Number</th>
            <th class="border px-4 py-2">Relation</th>
            <th class="border px-4 py-2">Cover Amount</th>
            <th class="border px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(method, index) in post_payment_methods" :key="index">
            <td class="border text-center px-4 py-2">{{ index + 1 }}</td>
            <td class="border text-center px-4 py-2">{{ method.company?.name || 'N/A' }}</td>
            <td class="border text-center px-4 py-2">{{ method.beneficiary?.name || 'N/A' }}</td>
            <td class="border text-center px-4 py-2">{{ method.member_number || '-' }}</td>
            <td class="border text-center px-4 py-2">{{ method.relation || '-' }}</td>
            <td class="border text-center px-4 py-2">KES {{ formatCurrency(method.cover_amount) }}</td>
            <td class="border text-center px-4 py-2">
              <span :class="method.is_active ? 'text-green-600' : 'text-red-600'">
                {{ method.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="(!pre_payment_methods || pre_payment_methods.length === 0) && (!post_payment_methods || post_payment_methods.length === 0)" class="text-gray-500 text-center py-4">
      No payment methods configured
    </div>

    <!-- ══════════════════════════════════════════════════════════════════
         DOCUMENTS SECTION — refactored
         • Grid of thumbnails (click → lightbox viewer on same page)
         • "Remove Document" button on each card
         • "Upload Another Document" button opens inline S3 uploader
    ════════════════════════════════════════════════════════════════════ -->
    <div class="mt-6 mb-8">
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-xl font-semibold text-gray-800">Documents</h3>
        <button
          @click="showUploadArea = !showUploadArea"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
          </svg>
          {{ showUploadArea ? 'Cancel Upload' : 'Upload Another Document' }}
        </button>
      </div>

      <!-- Inline upload area -->
      <div v-if="showUploadArea" class="mb-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
        <p class="text-sm font-medium text-gray-700 mb-4">Select a file (Images or PDF, max 5 MB)</p>

        <!-- Drop zone -->
        <div
          class="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors"
          :class="isDragging ? 'border-custom-yellow bg-yellow-50' : 'border-gray-300 hover:border-custom-yellow hover:bg-gray-100'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
          @click="$refs.docFileInput.click()"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="mx-auto h-10 w-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
          </svg>
          <p class="text-sm text-gray-600">
            <span class="font-medium text-custom-yellow">Click to browse</span> or drag and drop
          </p>
          <p class="text-xs text-gray-400 mt-1">PNG, JPG, PDF — up to 5 MB</p>
          <input
            ref="docFileInput"
            type="file"
            accept="image/*,.pdf"
            class="hidden"
            @change="onFileSelected"
          />
        </div>

        <!-- Selected file preview + Upload button -->
        <div v-if="pendingFile" class="mt-4 flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200">
          <img
            v-if="pendingPreviewUrl && pendingFile.type.startsWith('image/')"
            :src="pendingPreviewUrl"
            class="h-16 w-16 object-cover rounded flex-shrink-0"
            alt="preview"
          />
          <div v-else class="h-16 w-16 flex items-center justify-center bg-red-50 rounded flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-700 truncate">{{ pendingFile.name }}</p>
            <p class="text-xs text-gray-400">{{ formatFileSize(pendingFile.size) }}</p>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              @click="uploadDocument"
              :disabled="isUploading"
              class="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg v-if="isUploading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              {{ isUploading ? 'Uploading…' : 'Upload' }}
            </button>
            <button @click="clearPendingFile" class="p-1.5 text-gray-400 hover:text-gray-600 transition">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <p v-if="uploadError" class="mt-2 text-sm text-red-500">{{ uploadError }}</p>
      </div>

      <!-- Document thumbnail grid -->
      <div v-if="uploaded_documents && uploaded_documents.length > 0"
        class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        <div
          v-for="doc in uploaded_documents"
          :key="doc.DocumentId"
          class="relative group rounded-lg overflow-hidden border border-gray-200 shadow-sm bg-gray-50"
        >
          <!-- Thumbnail — clicking opens the on-page lightbox -->
          <div class="relative cursor-pointer" @click="openLightbox(doc)">
            <img
              v-if="doc.FileType && doc.FileType.startsWith('image/')"
              :src="doc.FileURL"
              :alt="doc.FileName"
              class="w-full h-36 object-cover transition group-hover:brightness-90"
            />
            <div v-else class="w-full h-36 flex flex-col items-center justify-center bg-gray-100 text-gray-400 transition group-hover:bg-gray-200">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 mb-1 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
              <span class="text-xs font-medium">PDF</span>
            </div>
            <!-- Hover hint -->
            <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition pointer-events-none">
              <span class="bg-black/50 text-white text-xs px-2 py-1 rounded">Click to view</span>
            </div>
          </div>

          <!-- Card footer: filename + Remove button -->
          <div class="flex items-center justify-between px-2 py-1.5 bg-white border-t border-gray-100">
            <p class="text-xs text-gray-600 truncate flex-1 mr-1" :title="doc.FileName">
              {{ doc.FileName || 'Document' }}
            </p>
            <button
              @click.stop="deleteDocument(doc)"
              :disabled="deletingDocId === doc.DocumentId"
              title="Remove Document"
              class="flex-shrink-0 flex items-center gap-1 text-red-400 hover:text-red-600 transition disabled:opacity-40 text-xs font-medium"
            >
              <svg v-if="deletingDocId === doc.DocumentId"
                class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Remove
            </button>
          </div>
        </div>
      </div>

      <div v-else-if="!showUploadArea" class="text-gray-500 text-center py-4">
        No documents uploaded
      </div>
    </div>

    <div v-if="isEditing" class="flex justify-end mt-4">
      <button
        @click="saveChanges"
        class="bg-custom-yellow text-white px-4 py-2 rounded mr-2"
        :disabled="submitting"
      >
        <span v-if="!submitting">Submit</span>
        <span v-else><img :src="loaderUrl" /></span>
      </button>
      <button @click="cancelEdit" class="bg-gray-300 text-gray-700 px-4 py-2 rounded">Cancel</button>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════════════════
       DOCUMENT LIGHTBOX VIEWER
       Teleported to <body> so it renders above all layout stacking contexts.
       • Image files → <img> rendered inline at full size
       • PDF files   → <iframe> embedded inline (no new tab)
       • Other types → Download button (only option for unsupported formats)
       • "Upload Another Document" closes viewer and opens upload area
       • "Remove Document" deletes from backend and closes viewer
  ════════════════════════════════════════════════════════════════════════ -->
  <Teleport to="body">
    <div
      v-if="lightboxDoc"
      class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
      @click.self="closeLightbox"
    >
      <div class="relative w-full max-w-4xl mx-4 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">

        <!-- Lightbox header -->
        <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200 flex-shrink-0">
          <p class="text-sm font-medium text-gray-700 truncate max-w-xs" :title="lightboxDoc.FileName">
            {{ lightboxDoc.FileName || 'Document' }}
          </p>
          <div class="flex items-center gap-2">

            <!-- Upload Another Document -->
            <button
              @click="openUploadFromLightbox"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
              </svg>
              Upload Another Document
            </button>

            <!-- Remove Document -->
            <button
              @click="deleteDocument(lightboxDoc)"
              :disabled="deletingDocId === lightboxDoc.DocumentId"
              class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg v-if="deletingDocId === lightboxDoc.DocumentId"
                class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Remove Document
            </button>

            <!-- Close -->
            <button
              @click="closeLightbox"
              class="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md transition"
              aria-label="Close viewer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Lightbox body -->
        <div class="flex-1 overflow-auto flex items-center justify-center bg-gray-50 p-4" style="max-height: 80vh;">
          <!-- Image -->
          <img
            v-if="lightboxDoc.FileType && lightboxDoc.FileType.startsWith('image/')"
            :src="lightboxDoc.FileURL"
            :alt="lightboxDoc.FileName"
            class="max-w-full object-contain rounded shadow"
            style="max-height: 75vh;"
          />
          <!-- PDF inline — no new tab -->
          <iframe
            v-else-if="lightboxDoc.FileType === 'application/pdf'"
            :src="lightboxDoc.FileURL"
            class="w-full rounded border-0"
            style="height: 75vh;"
            title="Document preview"
          />
          <!-- Unsupported: offer download as only option -->
          <div v-else class="flex flex-col items-center text-gray-500 py-16">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            <p class="text-sm font-medium text-gray-600 mb-1">{{ lightboxDoc.FileName }}</p>
            <p class="text-xs text-gray-400 mb-5">This file type cannot be previewed in the browser.</p>
            <a :href="lightboxDoc.FileURL" download
              class="px-4 py-2 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition">
              Download File
            </a>
          </div>
        </div>

      </div>
    </div>
  </Teleport>
</template>

<script>
import AWS from 'aws-sdk';
import apiClient from "@/services/api";
import avatarUrl from "@/assets/images/user.png";
import loaderUrl from "@/assets/images/loader.gif";

export default {
  name: "PatientInformation",
  components: {},
  props: {
    patientId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      // ── Patient ──────────────────────────────────────────────────────
      patientData: null,
      loading: true,
      error: null,
      isEditing: false,
      originalData: null,
      avatarUrl: avatarUrl,
      titles: ["Mr.", "Mrs.", "Miss", "Dr.", "Prof.", "Mx.", "Other"],
      genders: ["Male", "Female", "Other"],
      maritalStatuses: ["Single", "Married", "Divorced", "Widowed", "Separated", "Other"],
      relationships: ["Spouse", "Father", "Mother", "Brother", "Sister", "Gurdian", "Sibling", "Friend", "Child", "Colleague", "Other"],
      submitting: false,
      loaderUrl: loaderUrl,

      // ── Documents ────────────────────────────────────────────────────
      // Populated from response.data.uploaded_documents on fetch.
      // Each item shape: { DocumentId, FileName, FileType, FileURL }
      uploaded_documents: [],
      // The document currently open in the lightbox (null = closed)
      lightboxDoc: null,
      // DocumentId currently being deleted — drives per-card spinner
      deletingDocId: null,
      // Controls inline upload area visibility
      showUploadArea: false,
      isDragging: false,
      // File selected but not yet uploaded
      pendingFile: null,
      pendingPreviewUrl: null,
      isUploading: false,
      uploadError: null,

      // ── Payment ──────────────────────────────────────────────────────
      pre_payment_methods: [],
      post_payment_methods: [],
      drug_allergies: []
    };
  },

  watch: {
    patientData(patientData) {
      if (patientData.selected_method == "capitation" || patientData.selected_method == "ffs") {
        this.isInsurance = true;
      }
    },
  },

  computed: {
    filteredEmergencyContact() {
      return Object.entries(this.patientData?.emergency_contact || {})
        .filter(([key]) => key !== "EmergencyContactId")
        .filter(([key]) => key !== "PatientId")
        .reduce((acc, [key, value]) => { acc[key] = value; return acc; }, {});
    },
    filteredInsurance() {
      return Object.entries(this.patientData?.insurance || {})
        .filter(([key]) => key !== "EmergencyContactId")
        .filter(([key]) => key !== "PatientId")
        .reduce((acc, [key, value]) => { acc[key] = value; return acc; }, {});
    },
  },

  methods: {
    // ── Formatting ──────────────────────────────────────────────────────
    formatLabel(key) {
      return key.split(/(?=[A-Z])/).join(" ").replace(/^\w/, (c) => c.toUpperCase());
    },
    formatFileSize(bytes) {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    formatPaymentMethod(method) {
      const map = { cash: 'Cash', mpesa: 'M-Pesa', airtel_money: 'Airtel Money', card: 'Card' };
      return map[method] || method;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    },

    // ── Patient fetch ───────────────────────────────────────────────────
    async fetchPatientData() {
      try {
        this.loading = true;
        const response = await apiClient.getPatient(this.patientId);
        if (response.success) {
          this.patientData = response.data.patient;
          this.patientData.emergency_contact = response.data.emergency_contact || null;
          this.uploaded_documents            = response.data.uploaded_documents || [];
          this.pre_payment_methods           = response.data.pre_payment_methods || [];
          this.post_payment_methods          = response.data.post_payment_methods || [];
          this.drug_allergies = response.data.drug_allergies || [];
        } else {
          this.$toast.error("Unable to fetch patient information.");
        }
        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.error("fetchPatientData error:", error);
        this.$toast.error("Unable to fetch patient, please check your internet connection.");
      }
    },

    toggleEditMode() {
      if (!this.isEditing && !this.patientData.emergency_contact) {
        this.patientData.emergency_contact = { Name: '', RelationShipToPatient: '', PhoneNumber: '' }
      }
      this.isEditing = !this.isEditing;
    },
    async saveChanges() {
      this.submitting = true;
      try {
        const response = await apiClient.updatePatient(this.patientId, this.patientData);
        if (response.success) {
          this.$toast.success("Patient updated successfully");
          this.fetchPatientData();
        } else {
          this.$toast.error("Failed to update patient information.");
        }
        this.submitting = false;
        this.isEditing = false;
      } catch (error) {
        this.submitting = false;
        this.isEditing = false;
        this.$toast.error("Failed to update patient");
        console.error(error);
      }
    },
    cancelEdit() {
      const ec = this.patientData.emergency_contact

      if (ec && !ec.Name && !ec.EmergencyContactId, !ec.RelationShipToPatient && !ec.PhoneNumber) {
        this.patientData.emergency_contact = null
      }
      this.isEditing = false;
    },

    calculateAgeFromDob() {
      const birthDate = new Date(this.patientData.dob);
      const today = new Date();
      let monthDiff =
        (today.getFullYear() - birthDate.getFullYear()) * 12 +
        (today.getMonth() - birthDate.getMonth());
      if (today.getDate() < birthDate.getDate()) monthDiff--;
      if (monthDiff < 12) {
        this.patientData.ageType = 2;
        this.patientData.age = monthDiff;
      } else {
        this.patientData.ageType = 1;
        this.patientData.age = Math.floor(monthDiff / 12);
      }
    },
    calculateDobFromAge() {
      if (this.patientData.age !== null && this.patientData.age >= 0) {
        const today = new Date();
        const birthDate = new Date(today.getFullYear() - this.patientData.age, today.getMonth(), today.getDate());
        this.patientData.dob = birthDate.toISOString().split("T")[0];
      }
    },

    // ── Lightbox ────────────────────────────────────────────────────────
    openLightbox(doc) {
      this.lightboxDoc = doc;
    },
    closeLightbox() {
      this.lightboxDoc = null;
    },
    // Closes the lightbox and opens the upload area
    openUploadFromLightbox() {
      this.closeLightbox();
      this.showUploadArea = true;
    },

    // ── File selection / validation ─────────────────────────────────────
    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) this.setPendingFile(file);
      event.target.value = ''; // allow re-selecting the same file
    },
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setPendingFile(file);
    },
    setPendingFile(file) {
      this.uploadError = null;
      const MAX = 5 * 1024 * 1024;
      const ALLOWED = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'application/pdf'];
      if (!ALLOWED.includes(file.type)) {
        this.uploadError = 'Invalid file type. Please upload an image or PDF.';
        return;
      }
      if (file.size > MAX) {
        this.uploadError = 'File is too large. Maximum allowed size is 5 MB.';
        return;
      }
      if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingFile = file;
      this.pendingPreviewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    },
    clearPendingFile() {
      if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingFile = null;
      this.pendingPreviewUrl = null;
      this.uploadError = null;
    },

    // ── Upload to S3 → save URL to Rails ───────────────────────────────
    async uploadDocument() {
      if (!this.pendingFile) return;
      this.isUploading = true;
      this.uploadError = null;
      try {
        const s3 = new AWS.S3({
          endpoint:         process.env.VUE_APP_DO_ENDPOINT,
          signatureVersion: 'v4',
          accessKeyId:      process.env.VUE_APP_DO_ACCESS_KEY,
          secretAccessKey:  process.env.VUE_APP_DO_SECRET_KEY,
          region:           process.env.VUE_APP_DO_REGION,
        });

        const key = `patient-documents/${this.patientId}-${Date.now()}-${this.pendingFile.name}`;
        const result = await s3.upload({
          Bucket:      'jiji-bucket',
          Key:         key,
          Body:        this.pendingFile,
          ACL:         'public-read',
          ContentType: this.pendingFile.type,
        }).promise();

        // Persist the URL via Rails
        const response = await apiClient.addPatientDocument(this.patientId, {
          fileName: this.pendingFile.name,
          fileType: this.pendingFile.type,
          fileUrl:  result.Location,
        });

        if (response.success) {
          this.uploaded_documents.push({
            DocumentId: response.data?.DocumentId ?? `pending-${Date.now()}`,
            FileName:   this.pendingFile.name,
            FileType:   this.pendingFile.type,
            FileURL:    result.Location,
          });
          this.$toast.success('Document uploaded successfully.');
          this.clearPendingFile();
          this.showUploadArea = false;
        } else {
          this.uploadError = response.message || 'Failed to save document. Please try again.';
        }
      } catch (err) {
        console.error('uploadDocument error:', err);
        this.uploadError = 'Upload failed. Please check your connection and try again.';
      } finally {
        this.isUploading = false;
      }
    },

    // ── Delete document ─────────────────────────────────────────────────
    async deleteDocument(doc) {
      if (!confirm(`Remove "${doc.FileName || 'this document'}"?\nThis action cannot be undone.`)) return;
      this.deletingDocId = doc.DocumentId;
      try {
        const response = await apiClient.deletePatientDocument(doc.DocumentId);
        if (response.success) {
          this.uploaded_documents = this.uploaded_documents.filter(
            d => d.DocumentId !== doc.DocumentId
          );
          // Auto-close lightbox if that doc was open
          if (this.lightboxDoc?.DocumentId === doc.DocumentId) this.closeLightbox();
          this.$toast.success('Document removed successfully.');
        } else {
          this.$toast.error(response.message || 'Failed to remove document.');
        }
      } catch (err) {
        console.error('deleteDocument error:', err);
        this.$toast.error('Error removing document. Please try again.');
      } finally {
        this.deletingDocId = null;
      }
    },
  },

  beforeUnmount() {
    // Clean up any object URL we created for the file preview
    if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
  },

  mounted() {
    this.fetchPatientData();
  },
};
</script>